import React, { useEffect, useRef, useState } from "react";
import Drawer from "react-modern-drawer";
import { FaAngleDoubleRight, FaPhone, FaEnvelope, FaQuestionCircle, FaBook, FaSearch } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { IoSend } from "react-icons/io5";
import { MdSupportAgent, MdEmail, MdPhone, MdAttachFile } from "react-icons/md";
import { BiSupport } from "react-icons/bi";
import * as timeago from "timeago.js";
import { useGetFaqs } from "../../api/services/faqServices";
import NoData from "../../components/common/NoData";
import { useSelector } from "react-redux";

const Help = ({ isHelpOpen, toggleHelp }) => {
  const { data: faqs } = useGetFaqs();
  const { user } = useSelector((state) => state.auth);

  const [openIndex, setOpenIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [supportForm, setSupportForm] = useState({
    name: user?.fullName || "",
    email: user?.email || "",
    subject: "",
    description: "",
    priority: "medium",
    category: "",
  });

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleFormChange = (e) => {
    setSupportForm({ ...supportForm, [e.target.name]: e.target.value });
  };

  const handleSubmitSupport = (e) => {
    e.preventDefault();
    console.log("Support Form Submitted:", supportForm);
  };

  const filteredFaqs = faqs?.data?.filter((faq) =>
    faq.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.details.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const scrollRef = useRef();
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <Drawer
      open={isHelpOpen}
      onClose={toggleHelp}
      direction="right"
      className="p-4 sm:p-6 overflow-y-auto rounded-l-md bg-gray-50"
      size={typeof window !== 'undefined' && window.innerWidth < 640 ? '90vw' : window.innerWidth < 768 ? 400 : 480}
    >
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <div className="flex items-center gap-2">
          <BiSupport className="text-xl sm:text-2xl text-primary" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Help & Support</h2>
        </div>
        <FaAngleDoubleRight
          onClick={toggleHelp}
          className="cursor-pointer text-gray-600 hover:text-primary transition-colors text-lg sm:text-xl"
        />
      </div>

      <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-4 sm:mb-6">
        <button
          onClick={() => setActiveTab("overview")}
          className={`p-2 sm:p-2.5 text-center text-[0.65rem] sm:text-xs font-semibold rounded-lg transition-all duration-200 ${
            activeTab === "overview"
              ? "bg-primary text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
        >
          <MdSupportAgent className="mx-auto text-lg sm:text-xl mb-0.5 sm:mb-1" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab("faq")}
          className={`p-2 sm:p-2.5 text-center text-[0.65rem] sm:text-xs font-semibold rounded-lg transition-all duration-200 ${
            activeTab === "faq"
              ? "bg-primary text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
        >
          <FaQuestionCircle className="mx-auto text-lg sm:text-xl mb-0.5 sm:mb-1" />
          FAQs
        </button>
        <button
          onClick={() => setActiveTab("contact")}
          className={`p-2 sm:p-2.5 text-center text-[0.65rem] sm:text-xs font-semibold rounded-lg transition-all duration-200 ${
            activeTab === "contact"
              ? "bg-primary text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
        >
          <FaEnvelope className="mx-auto text-lg sm:text-xl mb-0.5 sm:mb-1" />
          Contact
        </button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === "overview" && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-gradient-to-r from-primary to-secondary text-white p-4 sm:p-6 rounded-xl mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2">Need Help?</h3>
              <p className="text-xs sm:text-sm text-blue-100">
                We're here to assist you with any questions or issues you may have.
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="bg-green-100 p-2 sm:p-3 rounded-full flex-shrink-0">
                    <FaPhone className="text-green-600 text-base sm:text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Call Us</h4>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">
                      Speak with our support team
                    </p>
                    <div className="space-y-1.5 sm:space-y-2">
                      <a
                        href="tel:+2202600001"
                        className="text-primary font-bold text-base sm:text-lg hover:underline block break-all"
                      >
                        +220 260 0001
                      </a>
                      <a
                        href="tel:1666"
                        className="text-green-600 font-bold text-base sm:text-lg hover:underline block"
                      >
                        1666 (Toll-Free)
                      </a>
                    </div>
                    <p className="text-[0.65rem] sm:text-xs text-gray-500 mt-1">
                      Available 24/7
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-full flex-shrink-0">
                    <FaEnvelope className="text-blue-600 text-base sm:text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Email Support</h4>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">
                      Send us a detailed message
                    </p>
                    <a
                      href="mailto:support@zappme.net"
                      className="text-primary font-semibold hover:underline text-sm sm:text-base break-all"
                    >
                      support@zappme.net
                    </a>
                    <p className="text-[0.65rem] sm:text-xs text-gray-500 mt-1">
                      We'll respond within 24 hours
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 p-3 rounded-full">
                    <FaQuestionCircle className="text-purple-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">Browse FAQs</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Find answers to common questions
                    </p>
                    <button
                      onClick={() => setActiveTab("faq")}
                      className="text-primary font-semibold hover:underline text-sm"
                    >
                      View All FAQs →
                    </button>
                  </div>
                </div>
              </div>

              {/* <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 p-3 rounded-full">
                    <FaBook className="text-orange-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">Documentation</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Detailed guides and tutorials
                    </p>
                    <a
                      href="#"
                      className="text-primary font-semibold hover:underline text-sm"
                    >
                      Read Documentation →
                    </a>
                  </div>
                </div>
              </div> */}
            </div>

            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Quick Tip:</strong> Before contacting support, check our FAQs section. You might find your answer instantly!
              </p>
            </div>
          </motion.div>
        )}

        {activeTab === "faq" && (
          <motion.div
            key="faq"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {faqs?.data?.length > 0 && (
              <div className="mb-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search FAQs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  />
                </div>
              </div>
            )}

            {filteredFaqs?.length === 0 ? (
              <NoData text={searchQuery ? "No FAQs match your search" : "No FAQs Added"} className={"mt-10"} />
            ) : (
              <div className="space-y-3">
                {filteredFaqs?.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="flex justify-between items-center w-full text-left p-4 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm font-semibold text-gray-800 pr-2">
                        {faq.title}
                      </span>
                      <span className="text-primary text-xl font-bold flex-shrink-0">
                        {openIndex === index ? "−" : "+"}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {openIndex === index && (
                        <motion.div
                          key="content"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-2 text-sm text-gray-600 bg-gray-50 border-t border-gray-200">
                            {faq.details}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "contact" && (
          <motion.div
            key="contact"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <MdPhone className="text-green-600 text-xl" />
                Prefer to Call?
              </h3>
              <div className="space-y-2">
                <a
                  href="tel:+2202600001"
                  className="block w-full bg-primary hover:bg-primary/90 text-white text-center py-3 rounded-lg font-semibold transition-colors duration-200"
                >
                  📞 Call +220 260 0001
                </a>
                <a
                  href="tel:1666"
                  className="block w-full bg-green-500 hover:bg-green-600 text-white text-center py-3 rounded-lg font-semibold transition-colors duration-200"
                >
                  📞 Call 1666 (Toll-Free)
                </a>
              </div>
              <p className="text-xs text-center text-gray-500 mt-2">
                Available 24/7 - We're always here to help
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <MdEmail className="text-primary text-xl" />
                Submit a Support Request
              </h3>
              
              <form onSubmit={handleSubmitSupport} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={supportForm.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={supportForm.email}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    name="category"
                    value={supportForm.category}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  >
                    <option value="">Select a category</option>
                    <option value="account">Account & Login</option>
                    <option value="payments">Payments & Billing</option>
                    <option value="products">Products & Inventory</option>
                    <option value="events">Events & Bookings</option>
                    <option value="technical">Technical Issues</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority Level
                  </label>
                  <select
                    name="priority"
                    value={supportForm.priority}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  >
                    <option value="low">Low - General inquiry</option>
                    <option value="medium">Medium - Need assistance</option>
                    <option value="high">High - Urgent issue</option>
                    <option value="critical">Critical - Business stopped</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={supportForm.subject}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="Brief description of your issue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={supportForm.description}
                    onChange={handleFormChange}
                    required
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm resize-none"
                    placeholder="Please provide detailed information about your issue..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <IoSend />
                  Submit Support Request
                </button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-4">
                We typically respond within 24 hours during business days
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Drawer>
  );
};

export default Help;
